import React, { useEffect, useState } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { Modal, Form, Col, Row, Input, Select, DatePicker, Divider, Upload, Button, message } from 'antd';
import moment from 'moment';
import { SOURCES, SOURCE_LABELS } from '../utils/constants';
import { getPublicationsUploadUrl } from '../api';

function PublicationModal(props: any) {
	const { visible, onClose, onSave, saving, publication, afterClose } = props;
	const [s3Keys, setS3Keys] = useState(publication.pdf_s3_keys || []);
	const [selectedSource, setSelectedSource] = useState(publication.source || '');
	const [publicationNumber, setPublicationNumber] = useState(publication.number || '');

	const [form] = Form.useForm();

	function saveForm(values: any) {
		if (publication.id) {
			values.id = publication.id;
		}

		if (moment.isMoment(values.issue_date)) {
			values.issue_date = moment(values.issue_date).format('YYYY-MM-DD');
		}
		if (moment.isMoment(values.effective_date)) {
			values.effective_date = moment(values.effective_date).format('YYYY-MM-DD');
		}

		values.pdf_s3_keys = s3Keys;

		onSave({
			...values,
		});
	}

	async function getFileUploadUrl(filename: string) {
		if (!selectedSource) {
			throw new Error('A source is required to upload a publication.');
		}

		try {
			const response = await getPublicationsUploadUrl({
				source: selectedSource,
				filename,
				number: publicationNumber,
			});
			const data = response;

			if (!response?.upload_url) {
				throw new Error('Unable to get a signed upload URL.');
			}

			setS3Keys([...s3Keys, data.pdf_s3_key]);
			return data.upload_url;
		} catch (error) {
			message.error('Unable to upload publication file. Something went wrong.');
			throw new Error('Unable to get a signed upload URL.');
		}
	}

	useEffect(() => {
		form.resetFields();
		setS3Keys(publication.pdf_s3_keys || []);
		setSelectedSource(publication.source || '');
		setPublicationNumber(publication.number || '');
	}, [publication, visible]);

	const uploadProps: UploadProps = {
		action: (file) => getFileUploadUrl(file.name),
		method: 'PUT',
		accept: '.pdf',
		onChange({ file }) {
			if (file.status === 'removed' || file.status === 'error') {
				setS3Keys(s3Keys.filter((key: string) => key?.split('/').pop() !== file.name));
			}
		},
		defaultFileList: s3Keys.length
			? s3Keys.map((s3Key: string) => ({
					uid: publication.id,
					name: s3Key?.split('/').pop(),
					status: 'done',
					url: publication.id
						? `${process.env.API_URL}/api/backoffice/publications/${publication.id}/download?file=${s3Key}`
						: undefined,
			  }))
			: [],
	};

	return (
		<Modal
			okText="Save"
			cancelText="Cancel"
			title={publication.id ? 'Edit Publication' : 'Add Publication'}
			okButtonProps={{ loading: saving }}
			visible={visible}
			onOk={() => form.submit()}
			destroyOnClose={true}
			onCancel={onClose}
			afterClose={afterClose}
		>
			<Form form={form} id="orgForm" onFinish={saveForm} layout="vertical">
				<Row gutter={16}>
					<Col span={24}>
						<Form.Item
							name="source"
							label="Source"
							initialValue={publication.source || ''}
							rules={[{ required: true, message: 'This field is required.' }]}
						>
							<Select onChange={(value) => setSelectedSource(value)}>
								{SOURCES.map((source) => (
									<Select.Option key={source} value={source}>
										{SOURCE_LABELS[source]}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="number"
							label="Number"
							initialValue={publication.number || ''}
							rules={[{ required: true, message: 'This field is required.' }]}
						>
							<Input placeholder="Number" onChange={(e) => setPublicationNumber(e.target.value)} />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="detail_url"
							label="Detail URL"
							initialValue={publication.detail_url || ''}
							rules={[{ required: true, message: 'This field is required.' }]}
						>
							<Input placeholder="Detail URL" />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item name="description" label="Description" initialValue={publication.description || ''}>
							<Input.TextArea placeholder="Description" />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="manufacturer"
							label="Manufacturer"
							initialValue={publication.manufacturer || []}
						>
							<Select mode="tags" placeholder="Select manufacturers"></Select>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item name="model" label="Model" initialValue={publication.model || []}>
							<Select mode="tags" placeholder="Select models"></Select>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="publication_type"
							label="Type"
							initialValue={publication.publication_type || ''}
						>
							<Input placeholder="Type" />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item name="issued_by" label="Issued By" initialValue={publication.issued_by || ''}>
							<Input placeholder="Issued By" />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="issue_date"
							label="Issue Date"
							initialValue={publication.issue_date ? moment(publication.issue_date) : undefined}
							rules={[{ required: true, message: 'This field is required.' }]}
						>
							<DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="effective_date"
							label="Effective Date"
							initialValue={publication.effective_date ? moment(publication.effective_date) : undefined}
						>
							<DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Divider />
			<Row>
				<Col span={24}>
					<Upload
						{...uploadProps}
						fileList={s3Keys.map((s3Key: string) => ({
							uid: s3Key,
							name: s3Key?.split('/').pop(),
							status: 'done',
							url: publication.id
								? `${process.env.API_URL}/api/backoffice/publications/${publication.id}/download?file=${s3Key}`
								: undefined,
						}))}
					>
						<Button disabled={saving || !selectedSource || !publicationNumber} icon={<UploadOutlined />}>
							Upload File
						</Button>
					</Upload>
				</Col>
			</Row>
		</Modal>
	);
}

export default PublicationModal;
